// frontend/src/components/TopNavbar.jsx
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaUserCircle, FaChevronDown, FaBell } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import { authHeaders } from '../utils/authHeaders';
import './TopNavbar.css';
import logo from '../assets/Logo.png';

/* --- Notifikasi hook --- */
const API = process.env.REACT_APP_API_URL || '/api';

function useNotifications(user) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchList = async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(
        `${API}/agriservis/notifications?user_id=${user.id}&limit=50`,
        { headers: authHeaders(user) }
      );
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      setItems(list);
      // backend mengembalikan is_read (boolean/0-1)
      setUnread(list.filter((n) => !n.is_read).length);
    } catch (e) {
      console.warn('notifications fetch error:', e);
    }
  };

  const markAllRead = async () => {
    if (!user?.id) return;
    try {
      const unreadIds = items.filter((n) => !n.is_read).map((n) => n.id);
      if (unreadIds.length === 0) return;
      await fetch(`${API}/agriservis/notifications/read`, {
        method: 'PATCH',
        headers: authHeaders(user, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ids: unreadIds, user_id: user.id }),
      });
      await fetchList();
    } catch (e) {
      console.warn('notification markRead error:', e);
    }
  };

  useEffect(() => {
    fetchList();
    const t = setInterval(fetchList, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return { items, unread, fetchList, markAllRead };
}

const TopNavbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { user, logout, isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const userMenuRef = useRef(null);
  const notifRef = useRef(null);
  const menuToggleRef = useRef(null);

  const { items: notifItems, unread, markAllRead } = useNotifications(user);
  const role = String(user?.role || '').toLowerCase();
  const canOpenUserManagement = ['admin', 'field team', 'management'].includes(role);

  const handleLogout = () => {
    logout();
    navigate('/register#login');
  };

  // Tutup dropdown saat klik di luar
  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (
        menuOpen &&
        !menuToggleRef.current?.contains(e.target) &&
        !document.querySelector('.navbar-center')?.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [dropdownOpen, notifOpen, menuOpen]);

  // Tutup hamburger menu saat route berubah
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const closeMenuOnClick = () => setMenuOpen(false);

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <img src={logo} alt="Sahabat Kakao" className="navbar-logo" />
      </div>

      {/* HAMBURGER (kanan, mobile) */}
      <div
        className="navbar-toggle"
        ref={menuToggleRef}
        onClick={() => setMenuOpen((o) => !o)}
        aria-label="Menu"
        aria-expanded={menuOpen}
      >
        <FaBars />
      </div>

      {/* MENU CENTER */}
      <div className={`navbar-center ${menuOpen ? 'active' : ''}`}>
        <ul className="navbar-menu">
          <li><Link to="/dashboard" onClick={closeMenuOnClick}>Dashboard</Link></li>
          <li><Link to="/pengembangandiri" onClick={closeMenuOnClick}>Pembelajaran Mandiri</Link></li>
          <li><Link to="/bisnissaya" onClick={closeMenuOnClick}>Bisnis Saya</Link></li>
          <li><Link to="/myorders" onClick={closeMenuOnClick}>Pesanan Pelanggan</Link></li>
          <li><Link to="/forum" onClick={closeMenuOnClick}>Forum Diskusi</Link></li>
          <li><Link to="/laporyuk" onClick={closeMenuOnClick}>Lapor Yuk!</Link></li>
        </ul>
      </div>

      {/* Ikon lonceng + user */}
      {isLoggedIn && (
        <div className="navbar-right">
          {/* NOTIF */}
          <div className="navbar-bell" ref={notifRef}>
            <button
              className="icon-btn"
              onClick={() => setNotifOpen((v) => !v)}
              aria-label="Notifikasi"
            >
              <FaBell />
              {unread > 0 && <span className="badge">{unread}</span>}
            </button>

            {notifOpen && (
              <div className="notif-panel">
                <div className="notif-head">
                  <div className="title">Notifikasi</div>
                  <button className="mark" onClick={markAllRead}>
                    Tandai semua terbaca
                  </button>
                </div>

                <div className="notif-list">
                  {notifItems.length === 0 ? (
                    <div className="notif-empty">Belum ada notifikasi.</div>
                  ) : (
                    notifItems.map((n) => (
                      <div key={n.id} className="notif-item">
                        <div className="notif-title">{n.title || 'Order'}</div>
                        <div className="notif-body">
                          {n.message || ''}
                          <br />
                          <small>{new Date(n.created_at).toLocaleString()}</small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* USER */}
          <div className="navbar-user" ref={userMenuRef}>
            <div
              className="user-toggle"
              onClick={() => setDropdownOpen((o) => !o)}
            >
              <FaUserCircle className="user-icon" />
              <span className="user-name">{user?.full_name || ''}</span>
              <FaChevronDown className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`} />
            </div>

            {dropdownOpen && (
              <ul className="dropdown-menu">
                <li><Link to="/profile">Profil Saya</Link></li>
                {user?.role === 'admin' && (
                  <>
                    <li><Link to="/register">Register User</Link></li>
                    <li><Link to="/admin-analytics">Viewer</Link></li>
                    <li><Link to="/survey-list">Survey List</Link></li>
                    <li><Link to="/survey-builder">Survey Builder</Link></li>
                    <li><Link to="/raw-data">Download Raw Data</Link></li>
                    <li><Link to="/coupons">Kupon Petani</Link></li>
                  </>
                )}
                {canOpenUserManagement && <li><Link to="/user-management">User Management</Link></li>}
                <li><button onClick={handleLogout}>Keluar</button></li>
              </ul>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default TopNavbar;
